import { MouseEvent, RefObject, useEffect, useRef, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import useScrollShadow from '@hooks/useScrollShadow';
import Text from '@atoms/Text/Text';
import Button from '@atoms/Button/Button';
import ProductListItem from '@molecules/ProductListItem/ProductListItem';
import EmptyCartModal from '@organisms/EmptyCartModal/EmptyCartModal';
import { makeProductEntries } from '@helpers/wishlistProductEntries';
import paths, { LoginQueryParams } from '@constants/paths';
import { selectMiniCartPreviewIsOpen, setIsCartPreviewOpen } from '@slices/miniCartSlice';
import Config from '@config';
import trackShoppingList from '@helpers/analyticsHelpers/trackShoppingList';
import {
  StyledHeader,
  StyledBody,
  StyledCartPreview,
  StyledEmptyCart,
  StyledButtonWrapper,
  StyledLoadingContainer,
  StyledSpinner,
  StyledPriceGrid,
  StyledLink,
  StyledButton,
} from './CartPreview.styles';
import Icon from '@atoms/Icon/Icon';
import { trackCartPreviewEmptyState } from '@helpers/analyticsHelpers/trackCartPreview';
import useCart from '@hooks/useCart';
import DynamicNamespaces from 'next-translate/DynamicNamespaces';
import useTranslation from 'next-translate/useTranslation';
import EmptyCart from '@molecules/EmptyCart/EmptyCart';
import AddToListComponent from '@organisms/AddToListComponent/AddToListComponent';
import AddToList from '@icons/add-to-list.svg';
import Close from '@icons/close.svg';
import Delete from '@icons/delete.svg';
import Backdrop from '@atoms/Backdrop/Backdrop';
import useResponsive from '@hooks/useResponsive';
import Heading from '@atoms/Heading/Heading';
import variables from '@root/styles/variables';

type Props = {
  headerRef: RefObject<HTMLDivElement>;
};

const CartPreview = ({ headerRef }: Props) => {
  const [isEmptyCartModalVisible, setEmptyCartModalVisibility] = useState(false);
  const [addToListModalOpen, setAddToListModalOpen] = useState(false);
  const { fromDesktop, belowDesktop } = useResponsive();
  const dispatch = useAppDispatch();

  const { t } = useTranslation('cartpreview');

  const { cart, isCartEmpty, hasFetchedCart } = useCart();
  const { products = [], totalUnitCount, subTotalWithDiscounts, totalPromotionDiscount } = cart || {};
  const totalWithDiscountText = subTotalWithDiscounts || t('cart->amountZero');
  const promotionDiscountText = totalPromotionDiscount || t('cart->amountZero');
  const hasProducts = products.length > 0;
  const isCartPreviewOpen = useAppSelector(selectMiniCartPreviewIsOpen);
  const productEntriesForWishlist = makeProductEntries(products);
  const hasWishlistProducts = productEntriesForWishlist?.length > 0;

  const cartPreviewRef = useRef<HTMLDivElement>(null);

  const { showTopShadow, showBottomShadow, setRef } = useScrollShadow();

  const queryParam = { from: LoginQueryParams.CART };

  const onGoToCart = (e: MouseEvent) => {
    if (isCartEmpty) e.preventDefault();
  };

  const cartHeightHandler = useCallback(() => {
    const stickyToolbarHeight = Config.HEIGHTS.STICKY_TOOLBAR;
    const cartElement = cartPreviewRef.current;
    if (!cartElement) return;

    const heightAttribute = CSS.supports('height: 100dvh') ? '100dvh' : '100vh';
    let newHeight = heightAttribute;

    if (fromDesktop) {
      const offsetHeight = (headerRef.current?.offsetHeight || 60) + stickyToolbarHeight;
      if (window.scrollY >= offsetHeight - stickyToolbarHeight) {
        newHeight = `calc(${heightAttribute} - ${stickyToolbarHeight}px)`;
      } else {
        newHeight = `calc(${heightAttribute} - ${offsetHeight - window.scrollY}px)`;
      }
    }
    cartElement.style.height = newHeight;
  }, [headerRef, fromDesktop]);

  // Run on mount
  useEffect(() => {
    if (!isCartPreviewOpen) return;
    cartHeightHandler();
  }, [cartHeightHandler, isCartPreviewOpen]);

  // Run on window resize (will also run when sticky toolbar sets height)
  useEffect(() => {
    if (typeof window === 'undefined' || !isCartPreviewOpen) return;

    const resizeObserver = new ResizeObserver(() => {
      cartHeightHandler();
    });

    resizeObserver.observe(window.document.body);

    return () => {
      resizeObserver.disconnect();
    };
  }, [cartHeightHandler, isCartPreviewOpen]);

  // Scrolling in window (desktop mode) requires handling of height because of the sticky toolbar
  useEffect(() => {
    if (!isCartPreviewOpen) return;

    document.addEventListener('scroll', cartHeightHandler);
    return () => {
      document.removeEventListener('scroll', cartHeightHandler);
    };
  }, [cartHeightHandler, isCartPreviewOpen]);

  useEffect(() => {
    // Focus on first button in the minicart when it opens, for A11y
    const firstButton = cartPreviewRef?.current?.querySelector('button');
    firstButton?.focus();
  }, []);

  useEffect(() => {
    if (hasFetchedCart && !hasProducts) {
      trackCartPreviewEmptyState();
    }
  }, [hasProducts, hasFetchedCart]);

  const closeHandler = () => {
    localStorage.setItem('willys.cart-mini', false.toString());
    dispatch(setIsCartPreviewOpen(false, cart));
  };

  return (
    <>
      <StyledCartPreview
        data-testid="cart-preview"
        ref={cartPreviewRef}
        isOpen={isCartPreviewOpen}
        role="complementary"
        aria-label={t('a11y->minicartLabel')}
      >
        <DynamicNamespaces namespaces={['cartpreview', 'cartpage']}>
          <StyledHeader showTopShadow={showTopShadow}>
            <Heading variant="h2" size="small">
              {t('cart->heading')}
            </Heading>
            <Button
              size="xsmall"
              theme="transparent"
              onClick={closeHandler}
              title={t('a11y->close')}
              aria-label={t('a11y->close')}
            >
              <Icon svg={Close} size={16} color={'black'} />
            </Button>
          </StyledHeader>

          {!hasFetchedCart ? (
            <StyledLoadingContainer>
              <Text type="subhead" size="small">
                {t('loading->title')}
              </Text>
              <StyledSpinner color="black" size={32} />
              <Text type="body" size="small">
                {t('loading->body')}
              </Text>
            </StyledLoadingContainer>
          ) : !hasProducts ? (
            <StyledEmptyCart>
              <EmptyCart fromCartPage={false} />
            </StyledEmptyCart>
          ) : (
            <StyledBody ref={setRef}>
              {products.map((product) => (
                <ProductListItem product={product} key={`cart-row-${product.name}-${product.code}`} />
              ))}
              <StyledButtonWrapper>
                <Button
                  theme="secondary"
                  onClick={() => setAddToListModalOpen(true)}
                  data-testid="add-to-list-button"
                  aria-label={t('button->saveToList')}
                  disabled={!hasWishlistProducts}
                >
                  <Icon svg={AddToList} size={20} color={!hasWishlistProducts ? 'darkerGray' : 'black'} />
                  {t('button->saveToList')}
                </Button>
                <Button
                  theme="secondary"
                  onClick={() => setEmptyCartModalVisibility(true)}
                  aria-label={t('button->clearCart')}
                  disabled={!hasProducts}
                >
                  <Icon svg={Delete} size={20} color={!hasProducts ? 'darkerGray' : 'black'} />
                  {t('button->clearCart')}
                </Button>
              </StyledButtonWrapper>
            </StyledBody>
          )}
          <StyledPriceGrid showBottomShadow={showBottomShadow}>
            <Text align="left" type="subhead" size="small">
              {t('cart->totalPrice', { count: totalUnitCount })}
            </Text>
            <Text align="right" type="price">
              {totalWithDiscountText}
            </Text>
            <Text align="left" type="body" size="small">
              {t('cart->youSave')}
            </Text>
            <Text align="right" type="price" size="small" color="red">
              {promotionDiscountText}
            </Text>
          </StyledPriceGrid>
          <StyledLink onClick={onGoToCart} href={paths.CART} prefetch={false}>
            <StyledButton theme="primary" disabled={!hasProducts}>
              {t('cart->goToCheckout')}
            </StyledButton>
          </StyledLink>
        </DynamicNamespaces>
      </StyledCartPreview>
      {isCartPreviewOpen && belowDesktop && (
        <Backdrop zIndex={variables.zIndex.cartPreview - 1} display={true} callback={closeHandler} />
      )}
      <EmptyCartModal
        emptyCartCallback={belowDesktop ? closeHandler : undefined}
        visible={isEmptyCartModalVisible}
        setVisibility={setEmptyCartModalVisibility}
        tracking={{ actionFieldList: 'minicart', category: 'minicart', action: 'removed_all_products_from_cart' }}
      />
      <AddToListComponent
        tracking={{ parent: 'minicart' }}
        addToListCallback={() => trackShoppingList({ action: 'current', parent: 'minicart' })}
        createNewListCallback={() => trackShoppingList({ action: 'new', parent: 'minicart' })}
        queryParamsIfNotLoggedIn={queryParam}
        productEntries={productEntriesForWishlist}
        onClose={() => setAddToListModalOpen(false)}
        isOpen={addToListModalOpen}
      />
    </>
  );
};

export default CartPreview;
